import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { graphBuilderApi } from '../../services/api';
import './Explainability.css';

/**
 * Reasoning Map
 *
 * Interactive evidence flow visualization showing how source documents
 * connect through evidence factors to decision rules and the final
 * decision.  All labels use business language only.
 */
const ReasoningMap = ({
  sourceDocuments = [],
  evidenceFactors = [],
  evidenceChain = [],
  decisionRules = [],
  contributingFactors = [],
  decision = '',
  confidence = '',
  executionId = '',
  insightId = '',
  domain = 'general',
  onFactorClick,
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showProvenance, setShowProvenance] = useState(false);
  const [provenance, setProvenance] = useState(null);
  const [provenanceLoading, setProvenanceLoading] = useState(false);

  useEffect(() => {
    if (!showProvenance || provenance || !executionId) return;
    let cancelled = false;
    setProvenanceLoading(true);
    graphBuilderApi.getProvenance(executionId, domain, null, insightId)
      .then(data => { if (!cancelled) setProvenance(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setProvenanceLoading(false); });
    return () => { cancelled = true; };
  }, [showProvenance, provenance, executionId, insightId, domain]);

  const maxInfluence = useMemo(() => {
    if (!contributingFactors.length) return 1;
    return Math.max(...contributingFactors.map(c => c.influence_pct || 0), 1);
  }, [contributingFactors]);

  const influenceMap = useMemo(() => {
    const map = {};
    contributingFactors.forEach(c => {
      map[c.factor_name] = c.influence_pct || 0;
    });
    return map;
  }, [contributingFactors]);

  const handleItemClick = useCallback((type, item) => {
    setSelectedItem({ type, ...item });
    if (onFactorClick) onFactorClick(type, item);
  }, [onFactorClick]);

  const filteredFactors = useMemo(() => {
    if (filter === 'all') return evidenceFactors;
    if (filter === 'strong') return evidenceFactors.filter(f =>
      (f.evidence_strength || '').toLowerCase() === 'strong'
    );
    if (filter === 'moderate') return evidenceFactors.filter(f =>
      (f.evidence_strength || '').toLowerCase() === 'moderate'
    );
    return evidenceFactors;
  }, [evidenceFactors, filter]);

  const getBubbleSize = (name) => {
    const influence = influenceMap[name] || 5;
    const normalized = influence / maxInfluence;
    return Math.max(40, Math.min(100, 40 + normalized * 60));
  };

  return (
    <div className="explain-reasoning-map">
      <h3 className="explain-section-title">Reasoning Map</h3>

      <div className="explain-map-filters">
        <button
          className={`explain-filter-btn ${filter === 'all' ? 'explain-filter-btn--active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Factors
        </button>
        <button
          className={`explain-filter-btn ${filter === 'strong' ? 'explain-filter-btn--active' : ''}`}
          onClick={() => setFilter('strong')}
        >
          Strong Only
        </button>
        <button
          className={`explain-filter-btn ${filter === 'moderate' ? 'explain-filter-btn--active' : ''}`}
          onClick={() => setFilter('moderate')}
        >
          Moderate+
        </button>
      </div>

      <div className="explain-map-flow">
        {/* Column 1: Source Documents */}
        <div className="explain-map-column">
          <h4 className="explain-map-column-title">Source Documents</h4>
          {sourceDocuments.map((doc, i) => (
            <div
              key={i}
              className="explain-map-bubble explain-map-bubble--doc"
              onClick={() => handleItemClick('document', doc)}
            >
              <span className="explain-map-bubble-label">{doc.document_name}</span>
              <span className="explain-map-bubble-meta">
                {doc.factors_contributed} factor(s)
              </span>
            </div>
          ))}
        </div>

        <div className="explain-map-arrows">
          {filteredFactors.length > 0 && (
            <div className="explain-map-arrow-label">identified as</div>
          )}
        </div>

        {/* Column 2: Evidence Factors */}
        <div className="explain-map-column">
          <h4 className="explain-map-column-title">Evidence Factors</h4>
          {filteredFactors.map((factor, i) => {
            const size = getBubbleSize(factor.name);
            const influence = influenceMap[factor.name] || 0;
            return (
              <div
                key={i}
                className={`explain-map-bubble explain-map-bubble--factor ${influence > 15 ? 'explain-map-bubble--highlight' : ''}`}
                style={{ minHeight: `${size}px` }}
                onClick={() => handleItemClick('factor', factor)}
              >
                <span className="explain-map-bubble-label">{factor.name}</span>
                <span className="explain-map-bubble-meta">
                  {factor.category} | {factor.evidence_strength}
                  {influence > 0 && ` | ${influence.toFixed(0)}% influence`}
                </span>
              </div>
            );
          })}
        </div>

        <div className="explain-map-arrows">
          {decisionRules.length > 0 && (
            <div className="explain-map-arrow-label">evaluated against</div>
          )}
        </div>

        {/* Column 3: Decision Rules */}
        <div className="explain-map-column">
          <h4 className="explain-map-column-title">Decision Rules</h4>
          {decisionRules.map((rule, i) => (
            <div
              key={i}
              className={`explain-map-bubble explain-map-bubble--rule ${rule.satisfied ? 'explain-map-bubble--satisfied' : 'explain-map-bubble--not-satisfied'}`}
              onClick={() => handleItemClick('rule', rule)}
            >
              <span className="explain-map-bubble-label">{rule.rule_name}</span>
              <span className="explain-map-bubble-meta">
                {rule.satisfied ? 'Satisfied' : 'Not Satisfied'}
              </span>
            </div>
          ))}
        </div>

        <div className="explain-map-arrows">
          {decision && (
            <div className="explain-map-arrow-label">determined</div>
          )}
        </div>

        {/* Column 4: Decision */}
        <div className="explain-map-column">
          <h4 className="explain-map-column-title">Decision</h4>
          <div className="explain-map-bubble explain-map-bubble--decision">
            <span className="explain-map-bubble-label">{decision}</span>
            <span className="explain-map-bubble-meta">{confidence}</span>
          </div>
        </div>
      </div>

      {/* Provenance Trail */}
      {executionId && (
        <div className="explain-provenance-section">
          <button
            className="explain-filter-btn"
            onClick={() => setShowProvenance(p => !p)}
          >
            {showProvenance ? 'Hide' : 'Show'} Provenance Trail
          </button>
          {showProvenance && (
            <div className="explain-provenance-content">
              {provenanceLoading ? (
                <p>Loading provenance...</p>
              ) : provenance?.provenance_chain ? (
                <div className="explain-provenance-chain">
                  {provenance.provenance_chain.map((step, i) => (
                    <div key={i} className="explain-provenance-step">
                      <span className="explain-provenance-type">{step.type || step.stage}</span>
                      <span className="explain-provenance-label">{step.label || step.description || step.name}</span>
                      {step.details && <span className="explain-provenance-detail">{step.details}</span>}
                    </div>
                  ))}
                </div>
              ) : (
                <p>No provenance data available.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Detail panel */}
      {selectedItem && (
        <div className="explain-map-detail-panel">
          <button className="explain-map-detail-close" onClick={() => setSelectedItem(null)}>
            Close
          </button>
          <h4>{selectedItem.name || selectedItem.document_name || selectedItem.rule_name}</h4>
          {selectedItem.type === 'factor' && (
            <div>
              <p><strong>Category:</strong> {selectedItem.category}</p>
              <p><strong>Source:</strong> {selectedItem.source_document}</p>
              {selectedItem.source_section && (
                <p><strong>Section:</strong> {selectedItem.source_section}</p>
              )}
              <p><strong>Evidence Strength:</strong> {selectedItem.evidence_strength}</p>
              {selectedItem.influence_on_decision && (
                <p><strong>Influence:</strong> {selectedItem.influence_on_decision}</p>
              )}
              {selectedItem.reasoning && (
                <p><strong>Reasoning:</strong> {selectedItem.reasoning}</p>
              )}
            </div>
          )}
          {selectedItem.type === 'rule' && (
            <div>
              <p><strong>Type:</strong> {selectedItem.rule_type}</p>
              <p><strong>Description:</strong> {selectedItem.rule_description}</p>
              <p><strong>Source Policy:</strong> {selectedItem.source_policy}</p>
              {selectedItem.criteria && (
                <ul>
                  {selectedItem.criteria.map((c, i) => (
                    <li key={i}>{c.satisfied ? '\u2713' : '\u2717'} {c.name}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {selectedItem.type === 'document' && (
            <div>
              <p><strong>Factors Contributed:</strong> {selectedItem.factors_contributed}</p>
              {selectedItem.sections_used && selectedItem.sections_used.length > 0 && (
                <p><strong>Sections Used:</strong> {selectedItem.sections_used.join(', ')}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReasoningMap;
